import * as THREE from 'three';
import type { PointoramaPlane } from '../../../../utils/PointoramaPlane';
import type { PlaneCreator } from '../../modules/PlaneCreator';
import type { LinePromptVisualizer } from '../../modules/line-detection/LinePromptVisualizer';
import type { CoordinateTransformer } from '../../modules/utils/CoordinateTransformer';
import type { LineSelectionHitTester } from '../../modules/line-detection/LineSelectionHitTester';
import type { ToolUndoRedoManager } from '../../ToolUndoRedoManager';
import type { LinePrompt } from '../../modules/line-detection/types';
import type { AnnotationKindAdapter } from './AnnotationKindAdapter';

export enum LineToolMode {
  AUTOMATIC = 'automatic',
  MANUAL = 'manual',
}

/**
 * Shared context that LineTool owns and exposes to both modes.
 * Modes store local aliases to these fields so they can use `this.visualizer` etc.
 */
export interface LineToolContext {
  // Shared visual infrastructure
  readonly planePreviewScene: THREE.Scene;
  readonly activePlane: PointoramaPlane;
  readonly planeCreator: PlaneCreator;
  readonly visualizer: LinePromptVisualizer;
  readonly coordinateTransformer: CoordinateTransformer;
  readonly hitTester: LineSelectionHitTester;
  readonly mouse: THREE.Vector2;

  // Refinement data (shared, survives mode switch)
  readonly planeClickDataByPromptId: Map<string, { center: THREE.Vector3; grid: THREE.Vector3[] }>;

  // Helpers
  getDirectionFromPlaneNormal(): THREE.Vector3;

  // Delegation
  dispatchToolPhase(phase: string): void;
  pushHintOverride(hintSourceString: string): void;
  popHintOverride(): void;
  isCameraDragging(): boolean;
  pushUndoSnapshot(): void;
  cancelPlaneRefinement(): void;
  readonly isRefining: boolean;
  readonly undoRedo: ToolUndoRedoManager | null;

  /** Per-kind plug-in: hit-test, view config, polygon pipeline path, commit dispatch. */
  readonly kindAdapter: AnnotationKindAdapter;
}

/**
 * Callbacks that LineTool needs from each mode for shared behavior
 * (plane clicks, refinement, etc.)
 */
export interface LineToolModeCallbacks {
  getLinePrompts(): LinePrompt[];
  getNextPromptId(): string;
  isPromptSphereDragging(): boolean;
  updateShapePreview(): void;
  addPrompt(prompt: LinePrompt): void;
}

/**
 * Snapshot of line prompt state for undo/redo.
 */
export interface PromptSnapshot {
  prompts: Array<{
    id: string;
    worldPoint: THREE.Vector3;
    direction: THREE.Vector3;
    screenPosition: THREE.Vector2;
    positiveLength?: number;
    negativeLength?: number;
    isTemporary?: boolean;
  }>;
  nextPromptId: number;
}

/**
 * Interface that line tool modes must implement to support undo/redo
 * delegation from LineTool (the BaseTool shell).
 */
export interface LineToolModeUndoRedo {
  /** Capture a deep-cloned snapshot of the current prompt state */
  captureUndoSnapshot(): PromptSnapshot;
  /** Restore prompt state from a snapshot (mode-specific visual updates) */
  restoreFromSnapshot(snapshot: PromptSnapshot): void;
  /** Prepare for an undo/redo operation (clear transient state like firstClickPoint) */
  prepareForUndoRedo(): void;
}
